import { CertificateGenerationDocumentId } from '~/store/covenants/types.ts';
import { BorrowerType } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { StateAidCertificateType } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import type {
  TDocumentGenerationOptions,
  TCertificateSaveResult,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import { DOCUMENT_GENERATION_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/constants.ts';
import type { TDocumentGenerationValidationIssues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/validation.ts';

export const getCertificateDocumentFlags = (documentId: string | number) => {
  const id = String(documentId);

  return {
    isFinancialCondition: id === CertificateGenerationDocumentId.FinancialCondition,
    isIncomeStructureByKved: id === CertificateGenerationDocumentId.IncomeStructureByKved,
    isStateAidReceived: id === CertificateGenerationDocumentId.StateAidReceived,
  };
};

export const buildDocumentGenerationOptions = ({
  documentId,
  borrowerType,
  stateAidCertificateType,
  regenerationSource,
}: {
  documentId: string | number;
  borrowerType: BorrowerType | null;
  stateAidCertificateType?: StateAidCertificateType;
  regenerationSource?: TApiApplicationDocument;
}): TDocumentGenerationOptions => {
  const { isFinancialCondition, isStateAidReceived } = getCertificateDocumentFlags(documentId);

  return {
    ...(isFinancialCondition ? { isFop: borrowerType === BorrowerType.SoleTrader } : {}),
    ...(isStateAidReceived && stateAidCertificateType ? { stateAidCertificateType } : {}),
    ...(regenerationSource ? { regenerationSource } : {}),
  };
};

export const isSuccessfulCertificateSave = (result: TCertificateSaveResult): boolean =>
  result === 'success';

type TBackendValidationError = {
  message?: unknown;
};

type TRejectedThunkError = {
  data?: unknown;
  message?: unknown;
};

export const getDocumentGenerationErrorMessages = (error: unknown): string[] => {
  const rejectedError = error as TRejectedThunkError | undefined;
  const responseData = rejectedError?.data;
  const messages = Array.isArray(responseData)
    ? responseData
        .map((item: TBackendValidationError) =>
          typeof item?.message === 'string' ? item.message.trim() : ''
        )
        .filter(Boolean)
    : [];

  const responseMessage =
    responseData && typeof responseData === 'object'
      ? (responseData as TBackendValidationError).message
      : undefined;
  const fallbackMessage =
    (typeof responseMessage === 'string' && responseMessage) ||
    (typeof rejectedError?.message === 'string' && rejectedError.message) ||
    'Не вдалося завантажити файл. Спробуйте ще раз.';

  return messages.length ? messages : [fallbackMessage];
};

export const getDocumentGenerationSaveTooltip = ({
  pendingHistoricalFieldNames,
  validationIssues,
}: {
  pendingHistoricalFieldNames: string[];
  validationIssues: TDocumentGenerationValidationIssues;
}): string =>
  [
    pendingHistoricalFieldNames.length &&
      `${DOCUMENT_GENERATION_LABELS.historicalPending}: ${pendingHistoricalFieldNames.length}`,
    validationIssues.requiredFieldNames.length &&
      `${DOCUMENT_GENERATION_LABELS.required}: ${validationIssues.requiredFieldNames.length}`,
    validationIssues.invalidFieldNames.length &&
      `${DOCUMENT_GENERATION_LABELS.invalid}: ${validationIssues.invalidFieldNames.length}`,
  ]
    .filter(Boolean)
    .join('\n');

export const getIsDocumentGenerationDisabled = ({
  isBusy,
  hasPendingHistoricalFields,
  isSaveableCertificate,
  isSaved,
  isFinancialCondition,
  hasBorrowerType,
  isStateAidReceived,
  hasStateAidCertificateType,
}: {
  isBusy: boolean;
  hasPendingHistoricalFields: boolean;
  isSaveableCertificate: boolean;
  isSaved: boolean;
  isFinancialCondition: boolean;
  hasBorrowerType: boolean;
  isStateAidReceived: boolean;
  hasStateAidCertificateType: boolean;
}): boolean =>
  isBusy ||
  hasPendingHistoricalFields ||
  (isSaveableCertificate && !isSaved) ||
  (isFinancialCondition && !hasBorrowerType) ||
  (isStateAidReceived && !hasStateAidCertificateType);
